<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import AgentLogsAPI from 'dashboard/api/agentLogs';

const rangeDays = ref(7);
const isLoading = ref(false);
const selectedAgentId = ref(null);
const detailsSection = ref(null);
const report = ref({
  summary: {},
  agents: [],
  recent_activity: [],
  agent_details: {},
});

const periodOptions = [
  { label: '7 dias', value: 7 },
  { label: '15 dias', value: 15 },
  { label: '30 dias', value: 30 },
];

const fetchLogs = async () => {
  isLoading.value = true;
  const to = Math.floor(Date.now() / 1000);
  const from = to - rangeDays.value * 24 * 60 * 60;

  try {
    const { data } = await AgentLogsAPI.get({ from, to });
    report.value = data;
    if (!selectedAgentId.value && data.agents?.length) {
      selectedAgentId.value = data.agents[0].id;
    }
  } catch {
    useAlert('Nao foi possivel carregar os logs dos agentes.');
  } finally {
    isLoading.value = false;
  }
};

const formatDate = value => {
  if (!value) return '-';

  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = seconds => {
  if (!seconds) return '-';

  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) return `${hours}h ${remainingMinutes}min`;
  return `${minutes}min`;
};

const summaryCards = computed(() => [
  {
    label: 'Agentes',
    value: report.value.summary?.agents_count || 0,
  },
  {
    label: 'Mensagens enviadas',
    value: report.value.summary?.messages_sent || 0,
  },
  {
    label: 'Conversas respondidas',
    value: report.value.summary?.conversations_responded || 0,
  },
  {
    label: 'Notas privadas',
    value: report.value.summary?.private_notes || 0,
  },
  {
    label: 'Tempo medio 1a resposta',
    value: formatDuration(report.value.summary?.average_first_response_seconds),
  },
]);

const selectedAgent = computed(() =>
  report.value.agents.find(agent => agent.id === selectedAgentId.value)
);

const selectedAgentDetails = computed(() => {
  if (!selectedAgentId.value) return null;
  return report.value.agent_details?.[selectedAgentId.value] || null;
});

const selectedAgentRecentActivity = computed(() => {
  if (!selectedAgentId.value) return report.value.recent_activity || [];

  return (report.value.recent_activity || []).filter(
    activity => activity.agent_id === selectedAgentId.value
  );
});

const selectAgent = async agent => {
  selectedAgentId.value = agent.id;
  await nextTick();
  detailsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const csvValue = value => `"${String(value || '').replaceAll('"', '""')}"`;

const exportCsv = () => {
  const rows = [
    [
      'Agente',
      'Email',
      'Ultimo login',
      'Login anterior',
      'Ultima atividade',
      'Conversas atuadas',
      'Conversas respondidas',
      'Mensagens enviadas',
      'Templates',
      'Notas privadas',
      'Tempo medio 1a resposta',
      'Tempo medio ate ultima resposta',
      'Conversas atribuidas',
      'Conversas resolvidas',
    ],
    ...report.value.agents.map(agent => [
      agent.name,
      agent.email,
      formatDate(agent.current_sign_in_at),
      formatDate(agent.last_sign_in_at),
      formatDate(agent.last_active_at),
      agent.conversations_touched,
      agent.conversations_responded,
      agent.messages_sent,
      agent.template_messages,
      agent.private_notes,
      formatDuration(agent.average_first_response_seconds),
      formatDuration(agent.average_last_response_seconds),
      agent.assigned_conversations,
      agent.resolved_conversations,
    ]),
  ];

  const blob = new Blob([rows.map(row => row.map(csvValue).join(';')).join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'logs-agentes.csv';
  link.click();
  URL.revokeObjectURL(link.href);
};

onMounted(fetchLogs);
</script>

<template>
  <main class="flex flex-col w-full min-h-screen bg-n-background">
    <header
      class="flex items-start justify-between gap-4 px-8 py-6 border-b border-n-weak"
    >
      <div>
        <h1 class="text-2xl font-semibold text-n-slate-12">Logs Agentes</h1>
        <p class="mt-1 text-sm text-n-slate-11">
          Atividade, respostas, logins e atendimento por agente.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <select
          v-model="rangeDays"
          class="h-10 px-3 text-sm border rounded-md border-n-strong bg-n-alpha-2 text-n-slate-12"
          @change="fetchLogs"
        >
          <option
            v-for="option in periodOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <button
          class="inline-flex items-center h-10 gap-2 px-3 text-sm font-medium border rounded-md border-n-strong bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3"
          @click="fetchLogs"
        >
          <span class="i-lucide-refresh-cw size-4" />
          Atualizar
        </button>
        <button
          class="inline-flex items-center h-10 gap-2 px-3 text-sm font-medium text-white rounded-md bg-n-brand hover:brightness-95"
          @click="exportCsv"
        >
          <span class="i-lucide-download size-4" />
          Exportar
        </button>
      </div>
    </header>

    <section class="flex flex-col gap-6 p-8">
      <div class="grid grid-cols-1 gap-3 md:grid-cols-5">
        <div
          v-for="card in summaryCards"
          :key="card.label"
          class="p-4 border rounded-lg border-n-weak bg-n-alpha-2"
        >
          <p class="text-xs font-medium uppercase text-n-slate-10">
            {{ card.label }}
          </p>
          <p class="mt-2 text-2xl font-semibold text-n-slate-12">
            {{ card.value }}
          </p>
        </div>
      </div>

      <p class="text-xs text-n-slate-10">
        Conversas atuadas considera conversas atribuidas ao agente, com resposta,
        nota privada ou participacao registrada no periodo.
      </p>

      <div
        v-if="isLoading"
        class="flex items-center justify-center h-40 text-sm text-n-slate-11"
      >
        <span class="i-lucide-loader-2 mr-2 size-5 animate-spin" />
        Carregando logs
      </div>

      <section v-else class="overflow-hidden border rounded-lg border-n-weak">
        <div class="px-4 py-3 border-b border-n-weak bg-n-alpha-2">
          <h2 class="text-base font-semibold text-n-slate-12">
            Desempenho por agente
          </h2>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-left bg-n-alpha-2 text-n-slate-10">
              <tr>
                <th class="px-4 py-3 font-medium">Agente</th>
                <th class="px-4 py-3 font-medium">Ultimo login</th>
                <th class="px-4 py-3 font-medium">Ultima atividade</th>
                <th class="px-4 py-3 font-medium">Atuadas</th>
                <th class="px-4 py-3 font-medium">Respondidas</th>
                <th class="px-4 py-3 font-medium">Msgs</th>
                <th class="px-4 py-3 font-medium">Templates</th>
                <th class="px-4 py-3 font-medium">Notas</th>
                <th class="px-4 py-3 font-medium">1a resposta</th>
                <th class="px-4 py-3 font-medium">Ate ultima</th>
                <th class="px-4 py-3 font-medium">Atribuidas</th>
                <th class="px-4 py-3 font-medium">Resolvidas</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="agent in report.agents"
                :key="agent.id"
                class="cursor-pointer border-t border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                :class="{ 'bg-n-blue-2': selectedAgentId === agent.id }"
                @click="selectAgent(agent)"
              >
                <td class="px-4 py-3">
                  <button
                    type="button"
                    class="text-left font-medium text-n-blue-11 hover:underline"
                    @click.stop="selectAgent(agent)"
                  >
                    {{ agent.name }}
                  </button>
                  <div class="text-xs text-n-slate-10">{{ agent.email }}</div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  {{ formatDate(agent.current_sign_in_at) }}
                  <div class="text-xs text-n-slate-10">
                    anterior: {{ formatDate(agent.last_sign_in_at) }}
                  </div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  {{ formatDate(agent.last_active_at) }}
                </td>
                <td class="px-4 py-3">{{ agent.conversations_touched }}</td>
                <td class="px-4 py-3">{{ agent.conversations_responded }}</td>
                <td class="px-4 py-3">{{ agent.messages_sent }}</td>
                <td class="px-4 py-3">{{ agent.template_messages }}</td>
                <td class="px-4 py-3">{{ agent.private_notes }}</td>
                <td class="px-4 py-3">
                  {{ formatDuration(agent.average_first_response_seconds) }}
                </td>
                <td class="px-4 py-3">
                  {{ formatDuration(agent.average_last_response_seconds) }}
                </td>
                <td class="px-4 py-3">{{ agent.assigned_conversations }}</td>
                <td class="px-4 py-3">{{ agent.resolved_conversations }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        v-if="selectedAgent && selectedAgentDetails"
        ref="detailsSection"
        class="overflow-hidden border rounded-lg border-n-weak"
      >
        <div
          class="flex flex-wrap items-start justify-between gap-3 px-4 py-3 border-b border-n-weak bg-n-alpha-2"
        >
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              Detalhes de {{ selectedAgent.name }}
            </h2>
            <p class="mt-1 text-xs text-n-slate-10">
              Login, conversas, mensagens, notas, templates e alterações registradas no período.
            </p>
          </div>
          <div class="text-xs text-n-slate-10">
            IP atual: {{ selectedAgentDetails.agent.current_sign_in_ip || '-' }}
            <span class="mx-2">|</span>
            IP anterior: {{ selectedAgentDetails.agent.last_sign_in_ip || '-' }}
          </div>
        </div>

        <div class="grid gap-4 p-4 xl:grid-cols-[22rem_minmax(0,1fr)]">
          <aside class="space-y-3">
            <div class="grid grid-cols-2 gap-2">
              <div class="rounded-md bg-n-alpha-2 p-3">
                <p class="text-xs text-n-slate-10">Msgs</p>
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ selectedAgentDetails.stats.messages_sent }}
                </p>
              </div>
              <div class="rounded-md bg-n-alpha-2 p-3">
                <p class="text-xs text-n-slate-10">Conversas</p>
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ selectedAgentDetails.stats.conversations_touched }}
                </p>
              </div>
              <div class="rounded-md bg-n-alpha-2 p-3">
                <p class="text-xs text-n-slate-10">Templates</p>
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ selectedAgentDetails.stats.templates }}
                </p>
              </div>
              <div class="rounded-md bg-n-alpha-2 p-3">
                <p class="text-xs text-n-slate-10">Notas</p>
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ selectedAgentDetails.stats.private_notes }}
                </p>
              </div>
            </div>

            <div class="max-h-[28rem] overflow-y-auto rounded-md border border-n-weak">
              <div class="sticky top-0 bg-n-alpha-2 px-3 py-2 text-xs font-semibold uppercase text-n-slate-10">
                Conversas
              </div>
              <button
                v-for="conversation in selectedAgentDetails.conversations"
                :key="conversation.id"
                type="button"
                class="block w-full border-t border-n-weak px-3 py-2 text-left hover:bg-n-alpha-2"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="truncate text-sm font-medium text-n-slate-12">
                    #{{ conversation.display_id }} {{ conversation.contact_name || '-' }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ formatDate(conversation.last_agent_message_at || conversation.last_activity_at) }}
                  </span>
                </div>
                <div class="mt-1 flex flex-wrap gap-1 text-xs text-n-slate-10">
                  <span v-if="conversation.current_stage" class="rounded bg-n-alpha-3 px-1.5 py-0.5">
                    {{ conversation.current_stage }}
                  </span>
                  <span class="rounded bg-n-alpha-3 px-1.5 py-0.5">
                    {{ conversation.messages_by_agent }} msg(s)
                  </span>
                  <span class="rounded bg-n-alpha-3 px-1.5 py-0.5">
                    {{ conversation.status }}
                  </span>
                </div>
              </button>
            </div>
          </aside>

          <div class="min-w-0 rounded-md border border-n-weak">
            <div class="sticky top-0 bg-n-alpha-2 px-3 py-2 text-xs font-semibold uppercase text-n-slate-10">
              Linha do tempo
            </div>
            <div class="max-h-[34rem] overflow-y-auto">
              <div
                v-for="event in selectedAgentDetails.timeline"
                :key="event.id"
                class="grid gap-2 border-t border-n-weak px-3 py-3 md:grid-cols-[9rem_12rem_minmax(0,1fr)]"
              >
                <div class="text-xs text-n-slate-10">
                  {{ formatDate(event.created_at) }}
                </div>
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ event.title }}
                  </p>
                  <p class="text-xs capitalize text-n-slate-10">
                    {{ event.type }}
                  </p>
                </div>
                <div class="min-w-0">
                  <p class="text-sm text-n-slate-12">
                    <span v-if="event.display_id">#{{ event.display_id }} </span>
                    {{ event.contact_name || '' }}
                  </p>
                  <p class="mt-1 whitespace-pre-wrap break-words text-xs text-n-slate-11">
                    {{ event.content || '-' }}
                  </p>
                  <p v-if="event.ip" class="mt-1 text-xs text-n-slate-10">
                    IP: {{ event.ip }}
                  </p>
                </div>
              </div>
              <div
                v-if="!selectedAgentDetails.timeline.length"
                class="px-3 py-8 text-center text-sm text-n-slate-10"
              >
                Nenhuma atividade detalhada no período.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        v-else-if="selectedAgent"
        ref="detailsSection"
        class="rounded-lg border border-n-weak bg-n-alpha-2 px-4 py-6 text-sm text-n-slate-11"
      >
        Nenhum detalhe retornado para {{ selectedAgent.name }} neste periodo.
      </section>

      <section class="overflow-hidden border rounded-lg border-n-weak">
        <div class="px-4 py-3 border-b border-n-weak bg-n-alpha-2">
          <h2 class="text-base font-semibold text-n-slate-12">
            Atividade recente de {{ selectedAgent?.name || 'todos os agentes' }}
          </h2>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-left bg-n-alpha-2 text-n-slate-10">
              <tr>
                <th class="px-4 py-3 font-medium">Horario</th>
                <th class="px-4 py-3 font-medium">Agente</th>
                <th class="px-4 py-3 font-medium">Acao</th>
                <th class="px-4 py-3 font-medium">Conversa</th>
                <th class="px-4 py-3 font-medium">Cliente</th>
                <th class="px-4 py-3 font-medium">Conteudo</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="activity in selectedAgentRecentActivity"
                :key="activity.id"
                class="border-t border-n-weak text-n-slate-12"
              >
                <td class="px-4 py-3 whitespace-nowrap">
                  {{ formatDate(activity.created_at) }}
                </td>
                <td class="px-4 py-3">{{ activity.agent_name }}</td>
                <td class="px-4 py-3 capitalize">{{ activity.action }}</td>
                <td class="px-4 py-3">#{{ activity.display_id }}</td>
                <td class="px-4 py-3">{{ activity.contact_name || '-' }}</td>
                <td class="max-w-lg px-4 py-3 truncate">
                  {{ activity.content || '-' }}
                </td>
              </tr>
              <tr v-if="!selectedAgentRecentActivity.length">
                <td
                  colspan="6"
                  class="border-t border-n-weak px-4 py-8 text-center text-sm text-n-slate-10"
                >
                  Nenhuma atividade recente para este agente no periodo.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  </main>
</template>
